import angleToColor from './angleToColor'
import { BasicEmotion, MoodWithEmotion } from 'db-tables'

export type MainEmotion = Pick<BasicEmotion, 'angle_start' | 'angle_end'> & { sector: number }
export type OverallEmotion = { sector: number; color: string }
export type SecondaryEmotion = BasicEmotion & { central_angle?: number }

const mainEmotions: MainEmotion[] = [
  { angle_start: 0, angle_end: 60, sector: 30 },
  { angle_start: 60, angle_end: 120, sector: 90 },
  { angle_start: 120, angle_end: 180, sector: 150 },
  { angle_start: 180, angle_end: 240, sector: 210 },
  { angle_start: 240, angle_end: 300, sector: 270 },
  { angle_start: 300, angle_end: 360, sector: 330 },
]

export function getMainEmotionColor(angle: number) {
  const mainEmotion = mainEmotions.find((mainEmotion) => {
    return isInRange(angle, mainEmotion.angle_start, mainEmotion.angle_end)
  })

  if (!mainEmotion) return

  return angleToColor(mainEmotion.sector)
}

export function findOverallEmotion(moods: MoodWithEmotion[]): OverallEmotion | undefined {
  const secondaryEmotions = extractEmotionsFromMoods(moods)
  const avarageAngle = findOverallAvarageAngle(secondaryEmotions)
  const color = angleToColor(avarageAngle)
  const foundMainEmotion = mainEmotions.find((mainEmotion) => {
    return isInRange(avarageAngle, mainEmotion.angle_start, mainEmotion.angle_end)
  })

  if (!foundMainEmotion) {
    return
  }

  return { color, sector: foundMainEmotion.sector }
}

function findOverallAvarageAngle(secondaryEmotions: SecondaryEmotion[]) {
  const centralAnglesSum = secondaryEmotions.reduce((accumulator, { central_angle }) => {
    return accumulator + (central_angle || 0)
  }, 0)

  return centralAnglesSum / secondaryEmotions.length
}

export function findEmotionCentralAngle(angleStart: number, angleEnd: number): number {
  const angleDelta = findAngleDelta(angleStart, angleEnd)
  const centralAngle = calculateCentralAngle(angleStart, angleDelta)
  return centralAngle
}

function findAngleDelta(angleStart: number, angleEnd: number): number {
  const isWheelOverRotated = angleStart > angleEnd
  if (isWheelOverRotated) {
    return 360 - angleStart + angleEnd
  }
  return angleEnd - angleStart
}

function calculateCentralAngle(angleStart: number, angleDelta: number) {
  let centralAngle = angleStart + angleDelta / 2

  if (centralAngle >= 360) {
    centralAngle = centralAngle - 360
  }

  return centralAngle
}

function isInRange(angle: number, angleStart: number, angleEnd: number): boolean {
  return angleStart <= angle && angle <= angleEnd
}

function extractEmotionsFromMoods(moods: MoodWithEmotion[]) {
  return moods.map<SecondaryEmotion>(({ emotion }) => ({
    id: emotion.id,
    name: emotion.name,
    angle_start: emotion.angle_start,
    angle_end: emotion.angle_end,
    central_angle: findEmotionCentralAngle(emotion.angle_start, emotion.angle_end),
  }))
}
